import React, { createContext, useContext, useEffect, useState } from "react";
import { useFirestore, useFunctions } from "reactfire";
import UserNotification from "../../shared/components/Notification/UserNotification";
import { Country, TeamRole } from "../../shared/constants/Constant";
import { useAppUser } from "../user-context/UserContext";
export interface TeamData {
  id?: string;
  //new fields
  logo?: string;
  industryTags?: string[];
  founded?: string;
  socialMediaLinks?: string[];
  companyName?: string;
  onLineIntro?: string;
  overview?: string;
  lastEditedBy?: Date;
  //end new fields
  name: string;
  project: string;
  theme?: string[];
  industries: string[];
  solvingProblem: string;
  solvingApproach: string;
  importance: string;
  stage: string;
  projectProgress: string;
  proposalLink: string;
  pitchLink?: string;
  active?: boolean;
  members: TeamMemberData[];
  country: Country;
  competitionProgress?: string;
  courses?: string[];
  sessions?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface TeamMemberData {
  id?: string;
  teamIds?: string[];
  fullName: string;
  name?: string;
  email: string;
  dob?: Date;
  university?: string;
  studying?: string;
  status?: string;
  state?: string;
  contact?: string;
  residence?: string;
  commitment?: string;
  motivation?: string;
  pastExperience?: string;
  cvLink?: string;
  role: TeamRole;
  country: Country;
  createdAt?: Date;
  updatedAt?: Date;
}
export interface ITeamsContext {
  teams: TeamData[];
  members: TeamMemberData[];
  getTeam: (id: string) => TeamData;
  getMember: (id: string) => TeamMemberData;
  updateTeamActiveStatus: (
    teamId: string,
    newActiveStatus: boolean,
    onStart?: () => void,
    onFinshied?: () => void
  ) => void;
}

const initialContext: ITeamsContext = {
  teams: [],
  members: [],
  getTeam: (id: string) => ({} as TeamData),
  getMember: (id: string) => ({} as TeamMemberData),
  updateTeamActiveStatus: (
    teamId: string,
    newActiveStatus: boolean,
    onStart?: () => void,
    onFinshied?: () => void
  ) => {},
};

const TeamsContext = createContext<ITeamsContext>(initialContext);

interface IProps {
  children: any;
}

export const TeamsProvider = (props: IProps) => {
  const store = useFirestore();
  const activeRef = useFunctions().httpsCallable("updateTeamActiveStatus");

  const appUser = useAppUser();
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [members, setMembers] = useState<TeamMemberData[]>([]);

  useEffect(() => {
    setMembers([]);
    let query:
      | firebase.firestore.Query
      | firebase.firestore.CollectionReference = store.collection("members");

    if (!appUser.userRole.isAdmin) {
      query = query.where("country", "==", appUser.country);
    }

    return query.onSnapshot({}, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data: any = change.doc.data();
          setMembers((ps) => {
            ps.push({
              ...data,
              id: change.doc.id,
            });

            return Object.assign([], ps);
          });
        }
        if (change.type === "modified") {
          setMembers((ps) => {
            const modified = ps.map((a) => {
              if (change.doc.id === a.id) {
                return {
                  ...(change.doc.data() as any),
                  id: change.doc.id,
                };
              } else {
                return a;
              }
            });

            return Object.assign([], modified);
          });
        }
        if (change.type === "removed") {
          setMembers((ps) => {
            const removed = ps.filter((a) => a.id !== change.doc.id);
            return Object.assign([], removed);
          });
        }
      });
    });
  }, [store, appUser.country, appUser.userRole]);

  useEffect(() => {
    setTeams([]);
    let query:
      | firebase.firestore.Query
      | firebase.firestore.CollectionReference = store.collection("teams");

    if (!appUser.userRole.isAdmin) {
      query = query.where("country", "==", appUser.country);
    }

    return query.onSnapshot({}, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data: any = change.doc.data();
          setTeams((ps) => {
            ps.push({
              ...data,
              id: change.doc.id,
              createdAt: change.doc.data().createdAt?.toDate(),
              updatedAt: change.doc.data().updatedAt?.toDate(),
            });

            return Object.assign([], ps);
          });
        }
        if (change.type === "modified") {
          setTeams((ps) => {
            const modified = ps.map((a) => {
              if (a.id === change.doc.id) {
                return {
                  ...(change.doc.data() as any),
                  id: change.doc.id,
                  createdAt: change.doc.data().createdAt?.toDate(),
                  updatedAt: change.doc.data().updatedAt?.toDate(),
                };
              } else {
                return a;
              }
            });

            return Object.assign([], modified);
          });
        }
        if (change.type === "removed") {
          setTeams((ps) => {
            const removed = ps.filter((a) => a.id !== change.doc.id);
            return Object.assign([], removed);
          });
        }
      });
    });
  }, [store, appUser]);

  const updateTeamActiveStatus = (
    teamId: string,
    newActiveStatus: boolean,
    onStart?: () => void,
    onFinshied?: () => void
  ) => {
    onStart && onStart();

    const ActiveStatusData = {
      teamID: teamId,
      active: newActiveStatus,
    };

    activeRef(ActiveStatusData)
      .then((data) => {
        UserNotification({
          isSuccess: true,
          message:
            "Team " +
            (newActiveStatus ? "activated " : "deactivated ") +
            "successfully!",
        });
      })
      .catch((error) => {
        UserNotification({
          isSuccess: false,
          message:
            "Team " +
            (newActiveStatus ? "activation " : "deactivation ") +
            "failed!\nPlease check your connection.",
        });
      })
      .finally(() => {
        onFinshied && onFinshied();
      });
  };

  const getTeam = (id: string): TeamData => {
    const [team] = teams.filter((t) => t.id === id);
    return team;
  };
  const getMember = (id: string): TeamMemberData => {
    const [member] = members.filter((m) => m.id === id);
    return member;
  };

  return (
    <TeamsContext.Provider
      value={{ teams, members, updateTeamActiveStatus, getTeam, getMember }}
    >
      {props.children}
    </TeamsContext.Provider>
  );
};

export const useTeams = (): ITeamsContext => useContext(TeamsContext);
